"use client"

import { useMemo } from "react"
import { SetupChecklistItem } from "@/components/presentational/tailwind/SetupChecklist"

/**
 * Hook to generate setup checklist items based on account state
 * Returns checklist items with completion status based on actual data
 */
export function useSetupChecklist(
    accountId: string | null,
    accountData?: {
        username?: string
        bio?: string
        avatar_url?: string
        targets?: any[]
        is_running?: boolean
        has_subscription?: boolean
        has_verified_channel?: boolean
    },
    callbacks?: {
        onStartGrowth?: () => void
        onAddTargets?: () => void
    }
): SetupChecklistItem[] {
    return useMemo(() => {
        if (!accountId) return []

        const settingsUrl = `/app/account/${accountId}/settings`

        return [
            {
                id: "connect-account",
                title: "Connect your account",
                description: "Link your Instagram or TikTok account to start growing.",
                actionLabel: "Connect account",
                actionHref: "/app",
                completed: !!accountId // Always true if we have an accountId
            },
            {
                id: "add-targets",
                title: "Add growth targets",
                description: "Choose hashtags and accounts to target for organic growth.",
                actionLabel: "Add targets",
                actionHref: callbacks?.onAddTargets ? undefined : settingsUrl,
                onAction: callbacks?.onAddTargets,
                completed: Boolean(accountData?.targets && accountData.targets.length > 0)
            },
            {
                id: "start-growth",
                title: "Start your growth",
                description: "Turn on growth mode to begin attracting new followers.",
                actionLabel: "Start growth",
                onAction: callbacks?.onStartGrowth,
                completed: Boolean(accountData?.is_running)
            },
            {
                id: "enable-reports",
                title: "Enable growth reports",
                description: "Get weekly updates on WhatsApp",
                actionLabel: "Enable reports",
                actionHref: "/app/settings/notifications",
                completed: Boolean(accountData?.has_verified_channel)
            }
        ]
    }, [accountId, accountData, callbacks])
}

/**
 * Default checklist items for when no account is selected
 */
export function useDefaultSetupChecklist(): SetupChecklistItem[] {
    return useMemo(
        () => [
            {
                id: "connect-account",
                title: "Connect your account",
                description: "Link your Instagram or TikTok account to start growing.",
                actionLabel: "Connect account",
                actionHref: "/app",
                completed: false
            },
            {
                id: "add-targets",
                title: "Add growth targets",
                description: "Choose hashtags and accounts to target for organic growth.",
                actionLabel: "Add targets",
                actionHref: "/app",
                completed: false
            },
            {
                id: "start-growth",
                title: "Start your growth",
                description: "Turn on growth mode to begin attracting new followers.",
                actionLabel: "Start growth",
                actionHref: "/app",
                completed: false
            },
            {
                id: "enable-reports",
                title: "Enable growth reports",
                description: "Get weekly updates on WhatsApp",
                actionLabel: "Enable reports",
                actionHref: "/app/settings/notifications",
                completed: false
            }
        ],
        []
    )
}
